/* cf-pickup-slot.js — Click Fencing, 2026-09-02
 *
 * THREE THINGS ON /pages/quote-review, ALL OF THEM ON THE ORDER.
 *
 * 1. A PREFERRED COLLECTION DAY AND TIME. Pete: "we need a preference for
 *    customers to select a time and day to pick up their order." A pickup order
 *    went through with no indication of when the customer wanted to come in, so
 *    the first anyone knew was when they turned up.
 *
 *    It mirrors the field already there: the details modal has "Preferred
 *    Delivery Date" (#qr-f-deliverydate), which the page HIDES on a pickup
 *    order. These two are its opposite number — shown only for pickup — and
 *    they reuse the same .qr-field markup so they need no CSS of our own.
 *
 *    It is a PREFERENCE, not a booking. Stock may not be in yet, so a blank
 *    never blocks checkout and every downstream wording says "asked for".
 *
 * 2. THE QUOTE REFERENCE. Measured: the note carried "Quote Name : Frameless
 *    Glass Quote 6" but not CF-GL-Z2NGVB, though the page shows it at the top.
 *    That missing line is why route-paid-orders could not reliably find the
 *    customer's own drawing to attach — quote NAMES repeat across customers, so
 *    a name is not a key and matching on one would eventually attach the wrong
 *    person's fence. The reference is unique and is already in the stored
 *    drawing's filename.
 *
 *    NOTE there are two reference formats in play: CF-GL-Z2NGVB (shown on the
 *    page, and what the drawing filename uses) and CF-FRAM-6-ec8a8087 (the
 *    "Quote Reference ID" cart attribute). We write the FIRST, because that is
 *    the one that joins to the drawing. The second is left alone deliberately —
 *    cf-plans and cf-account both key off it and unpicking that is its own job.
 *
 * 3. A PICKUP ORDER MUST NOT CARRY A DELIVERY DATE. Measured 2026-09-02 on a
 *    pickup order: the cart went to checkout with attribute
 *    "Delivery Date: 2026-09-03" — the hidden #qr-f-deliverydate still had its
 *    default selected. On a collection order that is a date nobody promised for
 *    a delivery that is not happening. We clear it and write Collection Day /
 *    Collection Time instead.
 *
 * ── WHY THIS TAKES OVER THE LAST WRITE RATHER THAN DECORATING ──────────────
 *
 * The first version of this file wrapped window.fetch and edited the outgoing
 * note. It logged "note decorated" every time and the lines still never reached
 * the cart. SIX other patches wrap window.fetch on this page —
 * cf-delivery-charge-once, cf-quote-editor, cf-unbuildable-guard,
 * cf-real-line-items, cf-one-price, quote-review-si-addon — and
 * cf-delivery-charge-once REBUILDS the note from its own source (it is what
 * prepends "FULFILMENT REQUESTED: PICKUP …"). Rebuilding beats decorating no
 * matter who wrapped first, so that approach cannot be made to work.
 *
 * So: let everyone else write whatever they like, then write LAST.
 * The wrapper awaits the real /cart/update.js, then re-reads /cart.js and PUTs
 * a corrected note and attributes back — and it does that BEFORE returning the
 * response, so the caller's own .then() (which navigates to checkout) cannot
 * run until the correction has landed. No navigation hooking, no race.
 *
 * Our own calls go through origFetch, never window.fetch, so they cannot
 * re-enter this wrapper or anybody else's.
 *
 * Nothing here touches a price, a product, a quantity or a cart line.
 */
(function () {
  'use strict';

  var TAG = '[cf-pickup-slot]';
  var DAYS_AHEAD = 21;            // calendar days scanned; weekends dropped
  var MAX_TRIES = 60;
  var tries = 0;

  /* 2026-09-26 (Pete): pickups are Mon-Fri 8am-2.30pm; the warehouse is open 7.30am-3pm.
     This is only the FALLBACK - the live windows come from fulfilment-rules (businesses row). */
  var WINDOWS = [
    '8:30 AM – 10:00 AM',
    '10:00 AM – 12:00 PM',
    '12:00 PM – 2:30 PM',
  ];

  var DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'];

  /* THE RULES LIVE ON THE businesses ROW, NOT HERE. 2026-09-11.
     WINDOWS and businessDays() above/below are now the FALLBACK. They said
     8am-3.30pm and "never today"; the row says 8.30am-3pm with ~4 hours to pick
     and pack, which earliest_fulfilment() turns into "same day if ordered before
     11am". The fulfilment-rules edge function serves the real days and windows
     (same-day windows already trimmed to now + lead time). Shared with
     cf-delivery-fix.js through window.__cfFulfilmentRulesP. */
  var RULES_URL = 'https://mhzmtafzxeshqqtktbwh.supabase.co/functions/v1/fulfilment-rules?business=click';
  function rulesPromise() {
    if (!window.__cfFulfilmentRulesP) {
      window.__cfFulfilmentRulesP = fetch(RULES_URL, { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) { if (j && j.ok) { window.__cfFulfilmentRules = j; return j; } return null; })
        .catch(function () { return null; });
    }
    return window.__cfFulfilmentRulesP;
  }
  function rules() {
    var R = window.__cfFulfilmentRules;
    return R && R.pickup_days && R.pickup_days.length ? R : null;
  }
  function label(iso) {
    var d = new Date(iso + 'T00:00:00');
    return DAY_NAMES[d.getDay()] + ' ' + d.getDate() + ' ' + MONTHS[d.getMonth()] + ' ' + d.getFullYear();
  }
  /* The ISO date behind a "Tuesday 8 September 2026" choice, for the
     machine-readable 'Collection Date' attribute. */
  function isoOfChoice() {
    var el = document.querySelector('#qr-f-pickupday');
    var op = el && el.options ? el.options[el.selectedIndex] : null;
    var v = op ? op.getAttribute('data-iso') : null;
    return v && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null;
  }

  function businessDays() {
    var out = [];
    var d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 1);              // never today - we have to order it in
    for (var i = 0; i < DAYS_AHEAD && out.length < 12; i++) {
      var day = d.getDay();
      if (day !== 0 && day !== 6) {
        out.push(DAY_NAMES[day] + ' ' + d.getDate() + ' ' + MONTHS[d.getMonth()] + ' ' + d.getFullYear());
      }
      d.setDate(d.getDate() + 1);
    }
    return out;
  }

  /* Is this order being collected?
     THE ACTIVE QUOTE IS THE ONLY HONEST SOURCE. sessionStorage.ullc_quote holds
     the quote the page is showing and its breakdown carries the very line the
     SERVER keys off - "Free Pickup from our Canning Vale Warehouse" vs a
     delivery line - so the page and route-paid-orders cannot disagree.
     The page's own text is NOT usable: /pages/quote-review prints neither
     string. And the delivery-date field's visibility is useless while the modal
     is shut, because everything in a closed modal reports hidden - that check
     alone would call every order a pickup. Both are last resorts. */
  function isPickup() {
    /* 2026-09-26 (Lenovo break test F14): sessionStorage.ullc_quote is shared by all four
       calculators and can still hold an EARLIER quote from another fence — a Blade pickup
       customer was once asked for a required Delivery Date because the leftover quote was a
       delivery one. The quote the page actually rendered (window.__cfViewedQuote, published by
       quote-review's hydrate() since 23 Sep) is read first; the old sources stay as fallbacks. */
    try {
      var vq = window.__cfViewedQuote;
      if (vq && vq.breakdown) {
        var vb = JSON.stringify(vq.breakdown);
        if (/pick ?up/i.test(vb)) return true;
        if (/flat rate delivery|delivery \(\+|metro delivery/i.test(vb)) return false;
      }
    } catch (e0) { /* fall through */ }
    try {
      var raw = sessionStorage.getItem('ullc_quote');
      if (raw) {
        var q = JSON.parse(raw);
        var blob = JSON.stringify(q.breakdown || q);
        if (/pick ?up/i.test(blob)) return true;
        if (/flat rate delivery|delivery \(\+|metro delivery/i.test(blob)) return false;
      }
    } catch (e) { /* fall through */ }
    try {
      var txt = document.body ? (document.body.innerText || '') : '';
      if (/Free Pickup|Pick ?up from our/i.test(txt)) return true;
      if (/Flat Rate Delivery|Delivery \(\+/i.test(txt)) return false;
      var dd = document.querySelector('#qr-f-deliverydate');
      var ov = document.querySelector('.qr-modal-overlay');
      var modalOpen = !!ov && getComputedStyle(ov).display !== 'none';
      if (dd && modalOpen) {
        var wrap = dd.closest('.qr-field') || dd.parentElement;
        if (wrap && wrap.offsetParent === null) return true;
      }
    } catch (e) { console.warn(TAG, 'isPickup', e); }
    return false;
  }

  function quoteReference() {
    try {
      if (!document.body) return null;
      var m = (document.body.innerText || '').match(/\bCF-[A-Z]{2}-[A-Z0-9]{4,}\b/);
      return m ? m[0] : null;
    } catch (e) { return null; }
  }

  function makeField(id, labelText, options, hint) {
    var wrap = document.createElement('div');
    wrap.className = 'qr-field';
    wrap.setAttribute('data-cf-slot', '1');
    var lab = document.createElement('label');
    lab.setAttribute('for', id);
    lab.textContent = labelText;
    var sel = document.createElement('select');
    sel.id = id;
    sel.name = id.replace('qr-f-', '');
    var blank = document.createElement('option');
    blank.value = '';
    blank.textContent = 'No preference';
    sel.appendChild(blank);
    options.forEach(function (o) {
      var op = document.createElement('option');
      var v = typeof o === 'string' ? o : o.value;
      op.value = v; op.textContent = v;
      if (o && o.iso) op.setAttribute('data-iso', o.iso);
      sel.appendChild(op);
    });
    if (rules()) wrap.setAttribute('data-cf-rules', '1');
    wrap.appendChild(lab);
    wrap.appendChild(sel);
    if (hint) {
      var p = document.createElement('p');
      p.style.cssText = 'margin:4px 0 0;font-size:12px;color:#5f6d6e';
      p.textContent = hint;
      wrap.appendChild(p);
    }
    return wrap;
  }

  function ensureFields() {
    try {
      var anchorEl = document.querySelector('#qr-f-deliverydate');
      if (!anchorEl) return;
      var anchor = anchorEl.closest('.qr-field') || anchorEl.parentElement;
      if (!anchor || !anchor.parentElement) return;

      var pickup = isPickup();
      var existing = document.querySelector('[data-cf-slot]');

      if (!pickup) {
        // Delivery order - our fields must not exist at all, so a stale value
        // can never ride along on a delivery note.
        if (existing) {
          var nodes = document.querySelectorAll('[data-cf-slot]');
          for (var i = 0; i < nodes.length; i++) nodes[i].parentNode.removeChild(nodes[i]);
        }
        return;
      }

      if (existing) return;

      var R = rules();
      var dayOpts = R
        ? R.pickup_days.map(function (d) { return { value: label(d.date), iso: d.date }; })
        : businessDays();
      var hint = R && R.text && R.text.pickup_hours
        ? 'Pickup ' + R.text.pickup_hours + ', at ' + (R.text.pickup_location || '28 Gauge Circuit, Canning Vale') + '.'
        : 'Pickup Monday to Friday, 8.30am to 2.30pm, at 28 Gauge Circuit, Canning Vale.';
      /* 2026-09-26: never "we will confirm when your kit is ready" - Pete, 25 Sep: a pickup
         customer is never told "we'll let you know". */
      var dayField = makeField('qr-f-pickupday', 'Preferred Collection Day', dayOpts, hint);
      var timeField = makeField('qr-f-pickuptime', 'Preferred Collection Time',
        R && R.pickup_windows ? R.pickup_windows : WINDOWS, '');

      anchor.parentElement.insertBefore(dayField, anchor.nextSibling);
      dayField.parentElement.insertBefore(timeField, dayField.nextSibling);

      /* A same-day choice only offers the windows that can still be packed in
         time - the edge function sends each day's own list. */
      if (R) {
        var daySel = dayField.querySelector('select');
        var timeSel = timeField.querySelector('select');
        daySel.addEventListener('change', function () {
          var iso = isoOfChoice();
          var wins = R.pickup_windows;
          for (var i = 0; i < R.pickup_days.length; i++) {
            if (R.pickup_days[i].date === iso && R.pickup_days[i].windows) wins = R.pickup_days[i].windows;
          }
          var keep = timeSel.value;
          while (timeSel.options.length > 1) timeSel.remove(1);
          wins.forEach(function (w) {
            var op = document.createElement('option');
            op.value = w; op.textContent = w;
            timeSel.appendChild(op);
          });
          timeSel.value = wins.indexOf(keep) >= 0 ? keep : '';
        });
      }
    } catch (e) { console.warn(TAG, 'ensureFields', e); }
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(function () {
      scheduled = false;
      try { ensureFields(); } catch (e) { console.warn(TAG, e); }
    }, 80);
  }

  function val(id) {
    var el = document.querySelector(id);
    var v = el && el.value ? String(el.value).trim() : '';
    return v.length ? v : null;
  }

  /* Insert "Label : value" after an existing line, keeping the note's shape. */
  function addLine(note, anchorRe, line) {
    var m = note.match(anchorRe);
    if (m) return note.replace(anchorRe, m[0] + '\n' + line);
    return note;
  }

  function decorateNote(note) {
    if (typeof note !== 'string' || !note.length) return note;
    var out = note;
    var ref = quoteReference();
    if (ref && !/^Quote Reference\s*:/m.test(out)) {
      out = addLine(out, /^Quote Name\s*:[^\n]*/m, 'Quote Reference : ' + ref);
    }
    if (isPickup()) {
      var day = val('#qr-f-pickupday');
      var time = val('#qr-f-pickuptime');
      if (day && !/^Pickup Day\s*:/m.test(out)) {
        out = addLine(out, /^Postcode\s*:[^\n]*/m, 'Pickup Day  : ' + day);
      }
      if (time && !/^Pickup Time\s*:/m.test(out)) {
        out = addLine(out, /^(Pickup Day\s*:[^\n]*|Postcode\s*:[^\n]*)/m, 'Pickup Time : ' + time);
      }
    }
    return out;
  }

  /* A pickup order carries no delivery date, and does carry the slot. Returns
     null when nothing needs changing so we can skip the write entirely. */
  function fixAttributes(attrs) {
    var a = attrs && typeof attrs === 'object' ? attrs : {};
    var next = {};
    for (var k in a) { if (Object.prototype.hasOwnProperty.call(a, k)) next[k] = a[k]; }
    var changed = false;
    if (isPickup()) {
      if (next['Delivery Date']) { next['Delivery Date'] = ''; changed = true; }   // '' removes it
      var day = val('#qr-f-pickupday');
      var time = val('#qr-f-pickuptime');
      if (day && next['Collection Day'] !== day) { next['Collection Day'] = day; changed = true; }
      if (time && next['Collection Time'] !== time) { next['Collection Time'] = time; changed = true; }
      /* Machine-readable twin of Collection Day, for shopify-poll -> fulfilment_date. */
      var dayIso = day ? isoOfChoice() : null;
      if (dayIso && next['Collection Date'] !== dayIso) { next['Collection Date'] = dayIso; changed = true; }
    }
    var ref = quoteReference();
    if (ref && next['Quote Reference Code'] !== ref) { next['Quote Reference Code'] = ref; changed = true; }
    return changed ? next : null;
  }

  /* THE LAST WRITE WINS, AND IT IS OURS. See the header for why decorating the
     outgoing body cannot work here. Loaded undeferred so document.body may not
     exist yet - nothing in this function may assume it does. */
  function installFetchWrap() {
    if (window.__cfPickupSlotFetchWrapped) return;
    window.__cfPickupSlotFetchWrapped = true;
    var origFetch = window.fetch;
    var correcting = false;

    async function correct() {
      var cart = await origFetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); });
      var note = decorateNote(cart.note || '');
      var attrs = fixAttributes(cart.attributes);
      var body = {};
      var need = false;
      if (note !== (cart.note || '')) { body.note = note; need = true; }
      if (attrs) { body.attributes = attrs; need = true; }
      if (!need) return false;
      await origFetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body),
      });
      console.log(TAG, 'cart corrected');
      return true;
    }

    window.fetch = async function (input, init) {
      var url = '';
      try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (e) {}
      var res = await origFetch.apply(this, arguments);
      // Only after a note-bearing update, and never re-entrantly.
      if (!correcting && /\/cart\/update/.test(url) && init && typeof init.body === 'string' &&
          init.body.indexOf('"note"') >= 0) {
        correcting = true;
        try { await correct(); }
        catch (e) { console.warn(TAG, 'correction failed', e); }
        finally { correcting = false; }
      }
      return res;
    };
    console.log(TAG, 'fetch wrapped');
  }

  function installDom() {
    if (window.__cfPickupSlotPatched) return true;
    if (!document.body) return false;
    window.__cfPickupSlotPatched = true;

    try {
      new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
    } catch (e) { console.warn(TAG, 'observer', e); }

    document.addEventListener('click', function (e) {
      if (e && e.target && e.target.closest && e.target.closest('#qr-enquire-btn')) schedule();
    }, true);

    ensureFields();
    /* Fields drawn from the fallback before the rules arrived are redrawn once. */
    rulesPromise().then(function (R) {
      if (!R) return;
      var old = document.querySelectorAll('[data-cf-slot]:not([data-cf-rules])');
      if (!old.length) return;
      for (var i = 0; i < old.length; i++) old[i].parentNode.removeChild(old[i]);
      ensureFields();
    });
    console.log(TAG, 'installed');
    return true;
  }

  installFetchWrap();

  if (/\/pages\/quote-review/.test(location.pathname)) {
    (function tryInstall() {
      tries++;
      var ok = false;
      try { ok = installDom(); } catch (e) { console.warn(TAG, 'install failed', e); }
      if (!ok && tries < MAX_TRIES) setTimeout(tryInstall, 500);
    })();
  }
})();
